import React, { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "app/components/ui/button";
import { VideoDialog } from "app/components/parts/video-dialog";
import {
  buildActivationCheckoutLocationState,
  saveActivationFlowData,
} from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";

const offerFeatures = [
  "Unlimited US calling",
  "Caller ID with name display",
  "Enhanced 911 emergency calling",
  "3-way conference calling",
  "Free calling to 170 countries",
  "Free number transfer service",
  "Voicemail with notifications",
  "No long-term contracts",
  "Call waiting & call holding",
  "30-day satisfaction guarantee",
];

export const OneTimeOffer = () => {
  const navigate = useNavigate();
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const acceptOfferLockRef = useRef(false);

  const handleAcceptOffer = useCallback(() => {
    if (acceptOfferLockRef.current) return;
    acceptOfferLockRef.current = true;
    try {
      saveActivationFlowData({ optForSecondMonthUpsell: true });
      navigate("/checkout", {
        state: buildActivationCheckoutLocationState(),
      });
    } catch {
      acceptOfferLockRef.current = false;
    }
  }, [navigate]);

  return (
    <React.Fragment>
      <div className="w-full max-w-[52.25rem] mt-3.5 sm:mt-4 md:mt-[1.125rem] lg:mt-5 sm:mb-5 md:mb-6 lg:mb-7 mx-auto">
        <div className="text-center bg-[#FFB9A7] px-5 sm:px-6 md:px-8 lg:px-10 pt-1.5 sm:pt-2 lg:pt-2.5 pb-2 sm:pb-2.5 lg:pb-3 border-2 border-dashed border-[#1D1D1B] rounded-xl mb-7 sm:mb-8 md:mb-9 lg:mb-10">
          <h3 className="font-mobile text-[#1D1D1B] text-[clamp(1.5rem,calc(2vw+1rem),2.25rem)] font-normal leading-[1.175] md:leading-[1.2] lg:leading-tight mb-1 sm:mb-1.5 lg:mb-2">
            Wait! Here&apos;s a One-time Offer: Get Additional 50% off Your
            Second Month of Service
          </h3>
          <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] md:text-base lg:text-lg font-normal leading-[1.125rem] sm:leading-5 md:leading-6 lg:leading-7">
            Enjoy additional savings with this one-time offer.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[13.1875rem_1fr] lg:grid-cols-[14.784375rem_1fr] gap-[1.125rem] sm:gap-5 md:gap-[1.375rem] lg:gap-6 2xl:gap-[1.625rem]">
          <Button
            type="button"
            variant="ghost"
            className="relative hover:bg-transparent w-full max-w-80 aspect-[0.7/1] sm:aspect-[0.65/1] rounded-xl md:rounded-2xl max-md:mx-auto overflow-hidden cursor-pointer group appearance-none border-none p-0"
            onClick={() => setShowVideoDialog(true)}
            style={{
              clipPath: "inset(0 round 0.75rem)",
            }}
          >
            <img
              src="/assets/one-time-offer-image.webp"
              alt="One time offer"
              className="size-full object-cover select-none"
              loading="lazy"
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 group-hover:bg-gradient-to-b group-hover:from-transparent group-hover:to-black/50 transition-all duration-300">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[4.2188rem] h-[3.0094rem] sm:w-[4.4531rem] sm:h-[3.1766rem] lg:w-[4.6875rem] lg:h-[3.34375rem] rounded-3xl transition-transform duration-300 group-hover:scale-110">
                <img
                  src="/assets/youtube-play-button.webp"
                  alt="Play button"
                  className="w-full h-full object-cover select-none"
                  loading="lazy"
                  draggable={false}
                />
              </div>
            </div>
          </Button>
          <div>
            <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-4 sm:leading-[1.125rem] md:leading-5 lg:leading-[1.375rem] mb-3 sm:mb-3.5 lg:mb-4">
              <span className="font-bold">
                Unlock extra savings with this limited-time offer.
              </span>{" "}
              Keep your number and continue enjoying our full-featured home
              phone service at a discounted price, get an additional month of
              service for only $6 (today only).
            </p>
            <ul className="list-none grid grid-cols-1 md:grid-cols-2 gap-x-4 sm:gap-x-5 lg:gap-x-6 gap-y-1.5 sm:gap-y-2 lg:gap-y-2.5 mb-4 sm:mb-[1.125rem] lg:mb-5">
              {offerFeatures.map((feature) => (
                <li
                  key={feature}
                  className="flex items-start gap-[0.375rem] sm:gap-[0.45rem] lg:gap-[0.56rem]"
                >
                  <span className="shrink-0 mt-[0.125rem]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 18 17"
                      fill="none"
                      className="size-[0.9375rem] sm:size-[1.0625rem] lg:size-[1.1875rem]"
                    >
                      <path
                        d="M6.5 8.69333L8.16667 10.36L11.5 7.0267M16.5 8.69333C16.5 12.8355 13.1421 16.1933 9 16.1933C4.85786 16.1933 1.5 12.8355 1.5 8.69333C1.5 4.55123 4.85786 1.19336 9 1.19336C13.1421 1.19336 16.5 4.55123 16.5 8.69333Z"
                        stroke="#1EC677"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <p className="text-[#141414] text-sm sm:text-[0.925rem] lg:text-base font-medium leading-5 sm:leading-[1.375rem] lg:leading-6">
                    {feature}
                  </p>
                </li>
              ))}
            </ul>
            <div className="flex flex-col max-sm:justify-center max-sm:items-center">
              <p className="font-mobile text-[#1D1D1B] text-2xl sm:text-[1.75rem] md:text-[2rem] lg:text-[2.25rem] font-medium leading-6 sm:leading-7 md:leading-8 lg:leading-9 mb-3.5 sm:mb-4 lg:mb-[1.125rem]">
                Get It Now for Just{" "}
                <span className="text-[#B2BAC6] line-through mx-0.5 sm:mx-1">
                  $30
                </span>{" "}
                $6
              </p>
              <Button
                type="button"
                variant="primary"
                className="gap-2 sm:gap-2.5 w-full max-w-fit h-10 sm:h-11 md:h-12 px-6 sm:px-7 rounded-full text-sm sm:text-[0.925rem] lg:text-base font-semibold leading-5 sm:leading-6 lg:leading-7"
                onClick={handleAcceptOffer}
              >
                <img
                  src="/assets/arrow-right-white-thick.webp"
                  alt="Arrow"
                  className="animate-nudge-right w-[1.0938rem] h-[0.7188rem] sm:w-[1.2031rem] sm:h-[0.7906rem] lg:w-[1.3125rem] lg:h-[0.8625rem]"
                />
                Yes, I Want This Deal
              </Button>
            </div>
          </div>
        </div>
      </div>
      <VideoDialog
        isOpen={showVideoDialog}
        onOpenChange={setShowVideoDialog}
        videoId="IniBFMJs748"
        aspectRatio="aspect-[9/16]"
      />
    </React.Fragment>
  );
};
